import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useCompanyData } from '~/data/company/useCompanyData';
import { useAllEmployees } from '~/data/employees';
import {
  EmploymentDetailType,
  useEmploymentDetails,
} from '~/data/employment/useEmploymentDetails';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { Enum } from '~/data/enums/types';
import {
  EmploymentDetailsProposalProps,
  EmploymentPositionProposalProps,
} from '~/data/proposals';
import {
  EmploymentProposalsDataAndConfig,
  filterAndSortEmploymentProposals,
} from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import { EmployeeProposalDecision } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { isRoleHR } from '~/services/Permissions';
import { filterActiveEnums } from '~/utils/helper';

import {
  mapEmploymentProposalDetailsPutData,
  submitEmplDetailsWorkflow,
} from './data';

export type PersonnelGroupValues = {
  [key: `PERSONNEL_GROUP_${number}`]: string;
};

export type PersonnelGroups = {
  fEmplDetailPersonnelGroup1: string;
  fEmplDetailPersonnelGroup2: string;
  fEmplDetailPersonnelGroup3: string;
  fEmplDetailPersonnelGroup4: string;
  fEmplDetailPersonnelGroup5: string;
};

export type ScheduleEmploymentDetailFormValues = {
  fEmplDetailProposalHrId?: number;
  fEmplDetailStatus: string;
  fEmplDetailJobGrade: string;
  fEmplDetailTaxNumber: string;
  fEmplDetailVacBalCalcSixDays: number;
  fEmplDetailCalcHoursBalance: string;
  fEmplDetailStartDate: moment.Moment;
  fEmplId?: number;
  customData: unknown;
} & PersonnelGroups;

export type UseScheduleEmploymentDetailOutput = {
  employmentStatuses: Enum[];
  jobGrades: Enum[];
  loading: boolean;
  employmentDetail?: EmploymentDetailType;
  defaultData: EmploymentDetailType;
  upcomingChangesOpen: boolean;
  fComMaxPersonnelGroups: number;
  proposals: EmploymentDetailsProposalProps[];
  isHRUser: boolean;
  newEngine: boolean;

  getPersonnelGroupOptions: (
    groupNumber: number,
    personnelGroupValues?: PersonnelGroupValues,
  ) => Enum[];
  setUpComingChangesOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onProposalDecision: (
    proposal: EmploymentDetailsProposalProps,
    decision?: EmployeeProposalDecision,
  ) => void;
};

type PropsType = {
  empId: number;
  resetFormAfterProposalDecision: ({
    emplDetPropData,
    empPositionPropData,
  }: {
    emplDetPropData?: EmploymentDetailsProposalProps;
    empPositionPropData?: EmploymentPositionProposalProps;
  }) => void;
  employmentDetailPending?: EmploymentDetailType;
  proposalsData: EmploymentProposalsDataAndConfig;
};

export const useScheduleEmploymentDetail = (
  props: PropsType,
): UseScheduleEmploymentDetailOutput => {
  const {
    empId,
    resetFormAfterProposalDecision,
    employmentDetailPending,
    proposalsData,
  } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Logged user
  const roles = useLoggedInUserRoles();

  // Proposals data
  const {
    details: { proposals: unsortedProposals, mutate: mutateProposals },
    config: { proposalsEnabled },
  } = proposalsData;
  const proposals = filterAndSortEmploymentProposals(
    unsortedProposals,
  ) as unknown as EmploymentDetailsProposalProps[];
  const newEngine = proposalsEnabled;

  // States
  const [upcomingChangesOpen, setUpComingChangesOpen] = useState(false);

  const { allEmployees, isLoading: employeesLoading } = useAllEmployees();
  const { companyData } = useCompanyData();
  const { fComMaxPersonnelGroups } = companyData.config;
  const {
    allEnums,
    isLoading: enumsLoading,
    failedToLoad: enumLoadFailed,
    getEnums,
  } = useAllEnums();
  const fEmpEmplId = allEmployees[empId].fEmpEmplId;
  const { data: employmentsDetail, isLoading } = useEmploymentDetails(
    empId,
    fEmpEmplId,
  );

  const employmentStatuses = useMemo(
    () => filterActiveEnums(getEnums(EnumListKey.EMPLOYMENT_STATUS)),
    [getEnums],
  );
  const jobGrades = useMemo(
    () => filterActiveEnums(getEnums(EnumListKey.JOB_GRADE)),
    [getEnums],
  );

  const getPersonnelGroupEnums = useCallback(
    (groupNumber: number, personnelGroupValues?: PersonnelGroupValues) => {
      const isValidGroupNumber = groupNumber >= 1 && groupNumber <= 5;
      const isLoaded = !enumsLoading && !enumLoadFailed;
      const personnelGroupEnumValues = filterActiveEnums(
        allEnums[`PERSONNEL_GROUP_${groupNumber}`],
      );
      const personnelGroupEnumValuesLinked: Enum[] = personnelGroupValues
        ? personnelGroupEnumValues.reduce((acc, cur) => {
            const tags = cur.tags;
            if (tags) {
              let newAcc = undefined;
              tags.forEach((tag) => {
                const personnelGroup = tag.split('__')[1];
                const personnelGroupCode = tag.split('__')[2];
                if (
                  personnelGroupValues[
                    personnelGroup as keyof PersonnelGroupValues
                  ] === personnelGroupCode
                ) {
                  newAcc = [...acc, cur];
                  return;
                }
              });
              if (newAcc) {
                return newAcc;
              }
            }
            return acc;
          }, [])
        : [];
      return isValidGroupNumber && isLoaded
        ? !!personnelGroupEnumValuesLinked.length
          ? personnelGroupEnumValuesLinked
          : personnelGroupEnumValues
        : [];
    },
    [allEnums, enumsLoading, enumLoadFailed],
  );

  const onProposalDecision = async (
    proposal: EmploymentDetailsProposalProps,
    decision?: EmployeeProposalDecision,
  ) => {
    const submitData = mapEmploymentProposalDetailsPutData(
      proposal,
      roles,
      decision,
    );
    await submitEmplDetailsWorkflow(
      empId,
      proposal.proposalData.ed.employment_fk,
      submitData,
      proposal.proposalId,
    ).then((r) => {
      if (r.error) {
        enqueueSnackbar(`${t(r.error)}`, {
          variant: 'error',
        });
      } else {
        resetFormAfterProposalDecision({
          //INFO: if it was a reject reset back to the last approved proposal
          emplDetPropData:
            proposals[decision === EmployeeProposalDecision.Reject ? 1 : 0],
        });
        mutateProposals();
      }
    });
  };

  return {
    employmentStatuses,
    jobGrades,
    getPersonnelGroupOptions: getPersonnelGroupEnums,
    loading: employeesLoading || enumsLoading || isLoading,
    employmentDetail: employmentDetailPending
      ? employmentDetailPending
      : employmentsDetail?.[0],
    defaultData: employmentsDetail
      ? employmentsDetail[0]
      : ({} as EmploymentDetailType),
    proposals,
    upcomingChangesOpen,
    setUpComingChangesOpen,
    fComMaxPersonnelGroups,
    onProposalDecision,
    isHRUser: isRoleHR(roles),
    newEngine,
  };
};
